"use client"

// Preview.js
import { useEffect, useState, useCallback } from "react"
import Slider from "react-slick"
import { getImagesByPianificazione, getImagesByBanner } from "../../../lib/api"
import { API_BASE } from "../../../lib/apiHelper"
import { devError, devWarn } from "../../../utils/devConsole"

const ADAPTIVE_COVER_RATIO_THRESHOLD = 2.2; // >= this ratio: prefer cover (no side bands)

function imgFetchProps(isPriority) {
  if (isPriority) {
    return { loading: "eager", fetchpriority: "high", decoding: "async" };
  }
  return { loading: "lazy", decoding: "async" };
}

function normalizeUrl(url) {
  if (!url) return "";
  const trimmed = url.trim();
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  if (trimmed.startsWith("www.")) return "https://" + trimmed;
  if (/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(trimmed)) return "https://" + trimmed;
  return "";
}

// --- Banner Preview ---
export function BannerPreview({ numP, refreshKey }) {
  return (
    <BasePreview
      numP={numP}
      refreshKey={refreshKey}
      entityType="banner"
      containerClass="bg-white w-full px-0"
      innerClass="w-full px-0"
      presentation="adaptive"
      imgClass="block w-full h-[180px] sm:h-[220px] md:h-[260px] lg:h-[280px]"
      fullBleed
    />
  );
}

// --- Planificazione Preview ---
export function PlanificazionePreview({ numP, refreshKey }) {
  return (
    <BasePreview
      numP={numP}
      refreshKey={refreshKey}
      entityType="planificazione"
      containerClass="bg-white w-full px-0"
      innerClass="w-full px-0"
      presentation="adaptive"
      imgClass="block w-full h-[240px] sm:h-[300px] md:h-[360px] lg:h-[420px]"
      fullBleed
    />
  );
}

// --- Full Width Banner Preview ---
export function FullWidthBannerPreview({ numP, refreshKey, prioritizeFirstImage = false }) {
  return (
    <BasePreview
      numP={numP}
      refreshKey={refreshKey}
      entityType="banner"
      containerClass="bg-white w-screen max-w-none px-0"
      innerClass="w-screen max-w-none px-0 bg-white"
      presentation="adaptive"
      prioritizeFirstImage={prioritizeFirstImage}
      imgClass="block w-full h-[180px] sm:h-[220px] md:h-[260px] lg:h-[280px]"
      slickArrows={false}
    />
  );
}

// --- BasePreview factorisé ---
function BasePreview({
  numP,
  refreshKey,
  entityType,
  containerClass,
  innerClass,
  imgClass,
  presentation,
  fullBleed = false,
  prioritizeFirstImage = false,
  slickArrows = true,
}) {
  const [images, setImages] = useState([]);
  const [ratiosById, setRatiosById] = useState({});
  const [imagesReady, setImagesReady] = useState(false);

  const storeRatio = useCallback((id, width, height) => {
    if (!id || !width || !height) return;
    const ratio = width / height;
    setRatiosById((prev) => {
      const existing = prev[id];
      if (existing && Math.abs(existing - ratio) < 0.01) return prev;
      return { ...prev, [id]: ratio };
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setImagesReady(false);
    setImages([]);
    setRatiosById({});

    const token = localStorage.getItem("authToken");
    if (entityType !== "banner" && !token) {
      devWarn("🔒 [PREVIEW] Utente non autenticato, immagini non caricate");
      setImagesReady(true);
      return undefined;
    }

    (async () => {
      try {
        const res =
          entityType === "banner"
            ? await getImagesByBanner(numP)
            : await getImagesByPianificazione(numP);
        if (cancelled) return;
        const filtered = res.filter((img) => typeof img.Ordine === "number" && img.Ordine >= 1);
        setImages(filtered);
      } catch (error) {
        if (!cancelled) {
          devError("Errore caricamento immagini:", error);
          setImages([]);
        }
      } finally {
        if (!cancelled) setImagesReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [numP, refreshKey, entityType]);

  const fullBleedStyle = fullBleed
    ? {
        width: "100vw",
        position: "relative",
        left: "50%",
        right: "50%",
        marginLeft: "-50vw",
        marginRight: "-50vw",
      }
    : undefined;

  if (!imagesReady) {
    return (
      <div
        id={"Anteprima" + numP}
        className={containerClass}
        style={fullBleedStyle}
      >
        <div className={innerClass}>
          <div
            className={`banner-preview-skeleton ${imgClass}`}
            aria-busy="true"
            aria-label="Caricamento"
          />
        </div>
      </div>
    );
  }

  if (images.length === 0) return null;

  const resolvePresentation = (imageId) => {
    if (presentation !== "adaptive") return presentation;
    const ratio = ratiosById[imageId];
    if (ratio !== undefined && ratio >= ADAPTIVE_COVER_RATIO_THRESHOLD) return "fixed-cover";
    return "fixed-contain-blur";
  };

  const settings = {
    autoplay: true,
    autoplaySpeed: 4000,
    dots: false,
    arrows: slickArrows,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  return (
    <div
      id={"Anteprima" + numP}
      className={containerClass}
      style={fullBleedStyle}
    >
      <div className={innerClass}>
        {images.length > 1 ? (
          <Slider {...settings}>
            {images.map((image, slideIndex) => {
              const isLcp = Boolean(prioritizeFirstImage && slideIndex === 0);
              const blurImgProps = imgFetchProps(false);
              const mainImgProps = imgFetchProps(isLcp);
              const imgSrc = image.File
                ? `${API_BASE.replace(/\/$/, "")}/${image.File.replace(/^\/+/, "").replace(/\\/g, "/")}?${new Date().getTime()}`
                : "/default.jpg";
              const shouldOpenInNewTab = image.NuovoTab === 1 && image.Link;
              const finalLink = image.Link ? normalizeUrl(image.Link) : "#";
              const altText = image.File || image.Nome || "Image";
              const mode = resolvePresentation(image.immagine_id);

              const content =
                mode === "fixed-contain-blur" ? (
                  <div className={`relative overflow-hidden bg-white ${imgClass}`}>
                    <img
                      src={imgSrc || "/placeholder.svg"}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full object-cover object-center blur-md scale-110 opacity-45"
                      {...blurImgProps}
                    />
                    <img
                      src={imgSrc || "/placeholder.svg"}
                      alt={altText}
                      onLoad={(e) => storeRatio(image.immagine_id, e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
                      className="relative z-10 mx-auto h-full w-auto max-w-full object-contain object-center"
                      {...mainImgProps}
                    />
                  </div>
                ) : mode === "fixed-cover" ? (
                  <div className={`relative overflow-hidden bg-white ${imgClass}`}>
                    <img
                      src={imgSrc || "/placeholder.svg"}
                      alt={altText}
                      onLoad={(e) => storeRatio(image.immagine_id, e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
                      className="w-full h-full object-cover object-center"
                      {...mainImgProps}
                    />
                  </div>
                ) : (
                  <img
                    className={imgClass}
                    alt={altText}
                    src={imgSrc || "/placeholder.svg"}
                    {...mainImgProps}
                  />
                );

              return (
                <div key={image.immagine_id} className="bg-white">
                  {shouldOpenInNewTab ? (
                    <a href={finalLink} target="_blank" rel="noopener noreferrer">
                      {content}
                    </a>
                  ) : (
                    content
                  )}
                </div>
              );
            })}
          </Slider>
        ) : (
          <div className="bg-white">
            {images.map((image, slideIndex) => {
              const isLcp = Boolean(prioritizeFirstImage && slideIndex === 0);
              const blurImgProps = imgFetchProps(false);
              const mainImgProps = imgFetchProps(isLcp);
              const imgSrcSingle = image.File
                ? `${API_BASE.replace(/\/$/, "")}/${image.File.replace(/^\/+/, "").replace(/\\/g, "/")}?${new Date().getTime()}`
                : "/default.jpg";
              const shouldOpenInNewTab = image.NuovoTab === 1 && image.Link;
              const finalLink = image.Link ? normalizeUrl(image.Link) : "#";
              const altText = image.File || image.Nome || "Image";
              const mode = resolvePresentation(image.immagine_id);

              const content =
                mode === "fixed-contain-blur" ? (
                  <div className={`relative overflow-hidden bg-white ${imgClass}`}>
                    <img
                      src={imgSrcSingle || "/placeholder.svg"}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full object-cover object-center blur-md scale-110 opacity-45"
                      {...blurImgProps}
                    />
                    <img
                      src={imgSrcSingle || "/placeholder.svg"}
                      alt={altText}
                      onLoad={(e) => storeRatio(image.immagine_id, e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
                      className="relative z-10 mx-auto h-full w-auto max-w-full object-contain object-center"
                      {...mainImgProps}
                    />
                  </div>
                ) : mode === "fixed-cover" ? (
                  <div className={`relative overflow-hidden bg-white ${imgClass}`}>
                    <img
                      src={imgSrcSingle || "/placeholder.svg"}
                      alt={altText}
                      onLoad={(e) => storeRatio(image.immagine_id, e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
                      className="w-full h-full object-cover object-center"
                      {...mainImgProps}
                    />
                  </div>
                ) : (
                  <img
                    className={imgClass}
                    alt={altText}
                    src={imgSrcSingle || "/placeholder.svg"}
                    {...mainImgProps}
                  />
                );

              return (
                <div key={image.immagine_id} className="bg-white">
                  {shouldOpenInNewTab ? (
                    <a href={finalLink} target="_blank" rel="noopener noreferrer">
                      {content}
                    </a>
                  ) : (
                    content
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
